import { useEffect } from "react";
import { fetchBranding } from "@/lib/api";

/**
 * Bullseye.so visitor-tracking pixel injector.
 *
 * Admin pastes the Pixel API key into `BULLSEYE_API_KEY` (Settings → Analytics)
 * and optionally restricts which public pages the pixel loads on via
 * `BULLSEYE_PAGES` (comma-separated tokens, e.g. "home,lander,sold").
 * An empty pages list means "run on every public page".
 *
 * The component takes a `page` prop naming the current surface so the
 * gating check can match the admin's allow-list. Idempotent — the script
 * only mounts once per page load and is removed cleanly on unmount.
 *
 * Reference: https://docs.bullseye.so/getting-started/install-tracking-pixel
 */
export default function BullseyeEmbed({ page }) {
  useEffect(() => {
    let mounted = true;
    let scriptEl = null;

    fetchBranding()
      .then((brand) => {
        if (!mounted) return;
        const apiKey = (brand?.bullseye_api_key || "").trim();
        if (!apiKey) return;

        const allow = (brand?.bullseye_pages || "")
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
        const current = (page || "").trim().toLowerCase();
        // Empty allow-list = run everywhere. Otherwise page must be listed.
        if (allow.length > 0 && current && !allow.includes(current)) return;

        if (document.getElementById("cd-bullseye-script")) return;

        scriptEl = document.createElement("script");
        scriptEl.id = "cd-bullseye-script";
        scriptEl.async = true;
        scriptEl.defer = true;
        scriptEl.src = `https://api.app.bullseye.so/api/v1/visitor-tracking/script/${encodeURIComponent(apiKey)}`;
        document.head.appendChild(scriptEl);
      })
      .catch(() => { /* silent — never break the page if branding fails */ });

    return () => {
      mounted = false;
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, [page]);

  return null;
}
